"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPCODE_PONG = exports.OPCODE_PING = exports.OPCODE_CLOSE = exports.OPCODE_BINARY = exports.OPCODE_TEXT = exports.OPCODE_CONTINUATION = exports.DecompressError = void 0;
exports.encodeWsFrame = encodeWsFrame;
exports.encodeFrame = encodeFrame;
exports.decodePayload = decodePayload;
exports.encodeDocument = encodeDocument;
exports.decodeDocument = decodeDocument;
exports.buildPongFrame = buildPongFrame;
exports.pdaDecompress = pdaDecompress;
exports.buildCloseAckFrame = buildCloseAckFrame;
const zlib_1 = __importDefault(require("zlib"));
const utils_1 = require("../utils");
class DecompressError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DecompressError';
    }
}
exports.DecompressError = DecompressError;
exports.OPCODE_CONTINUATION = 0x0;
exports.OPCODE_TEXT = 0x1;
exports.OPCODE_BINARY = 0x2;
exports.OPCODE_CLOSE = 0x8;
exports.OPCODE_PING = 0x9;
exports.OPCODE_PONG = 0x10;
/**
 * WebSocket-фрейм с управлением FIN/opcode (без маскирования, TCP :993).
 * Для `su`: TEXT fin=false → CONTINUATION, fin=true на последнем чанке.
 */
function encodeWsFrame(payload, { opcode = exports.OPCODE_TEXT, fin = true, compress = false } = {}) {
    let firstByte = opcode;
    if (fin)
        firstByte |= 0x80;
    let body = payload;
    if (compress) {
        firstByte |= 0x40;
        const deflated = zlib_1.default.deflateRawSync(payload);
        const sizeBuf = Buffer.alloc(4);
        sizeBuf.writeUInt32LE(payload.length, 0);
        body = Buffer.concat([sizeBuf, deflated]);
    }
    const parts = [Buffer.from([firstByte])];
    const len = body.length;
    if (len <= 125) {
        parts.push(Buffer.from([len]));
    }
    else if (len <= 65535) {
        const lenBuf = Buffer.alloc(2);
        lenBuf.writeUInt16BE(len, 0);
        parts.push(Buffer.from([126]), lenBuf);
    }
    else {
        const lenBuf = Buffer.alloc(8);
        lenBuf.writeUInt32BE(0, 0);
        lenBuf.writeUInt32BE(len, 4);
        parts.push(Buffer.from([127]), lenBuf);
    }
    parts.push(body);
    return Buffer.concat(parts);
}
/**
 * Собрать исходящий WS-фрейм без маскирования (как Android Network.SendDocument на TCP:993).
 */
function encodeFrame(payload, { compress = true, binary = false } = {}) {
    return encodeWsFrame(payload, {
        opcode: binary ? exports.OPCODE_BINARY : exports.OPCODE_TEXT,
        fin: true,
        compress: compress && !binary,
    });
}
function decodePayload(frameBuffer) {
    if (frameBuffer.length < 2) {
        throw new Error('Frame too short');
    }
    const firstByte = frameBuffer[0];
    const secondByte = frameBuffer[1];
    const opcode = firstByte & 0x0f;
    const compressed = (firstByte & 0x40) !== 0;
    let payloadLen = secondByte & 0x7f;
    let headerLen = 2;
    if (payloadLen === 126) {
        if (frameBuffer.length < 4)
            throw new Error('Incomplete extended length');
        payloadLen = frameBuffer.readUInt16BE(2);
        headerLen = 4;
    }
    else if (payloadLen === 127) {
        if (frameBuffer.length < 10)
            throw new Error('Incomplete extended length');
        payloadLen = frameBuffer.readUInt32BE(6);
        headerLen = 10;
    }
    const masked = (secondByte & 0x80) !== 0;
    if (masked)
        headerLen += 4;
    const totalLen = headerLen + payloadLen;
    if (frameBuffer.length < totalLen) {
        throw new Error('Incomplete frame body');
    }
    let payload = frameBuffer.subarray(headerLen, totalLen);
    if (masked) {
        const maskStart = headerLen - 4;
        const mask = frameBuffer.subarray(maskStart, maskStart + 4);
        const unmasked = Buffer.alloc(payload.length);
        for (let i = 0; i < payload.length; i++) {
            unmasked[i] = payload[i] ^ mask[i % 4];
        }
        payload = unmasked;
    }
    if (compressed) {
        payload = pdaDecompress(payload);
    }
    return { opcode, payload };
}
function encodeDocument(payload) {
    return Buffer.from((0, utils_1.encodePdaJson)(payload));
}
function decodeDocument(payloadBuffer) {
    const text = (0, utils_1.decodeFromWin1251)(payloadBuffer);
    return JSON.parse(text);
}
function buildPongFrame(pingPayload) {
    const firstByte = 0x80 | exports.OPCODE_PONG;
    const len = pingPayload.length;
    if (len <= 125) {
        return Buffer.concat([Buffer.from([firstByte, len]), pingPayload]);
    }
    const lenBuf = Buffer.alloc(2);
    lenBuf.writeUInt16BE(len, 0);
    return Buffer.concat([Buffer.from([firstByte, 126]), lenBuf, pingPayload]);
}
/**
 * Распаковка как в Android Inflater(nowrap): raw deflate, затем flush через 00 00 FF FF.
 * Node zlib требует finishFlush: Z_SYNC_FLUSH вместо явного хвоста (см. n0$a.smali).
 */
function pdaDecompress(payload) {
    if (payload.length < 4)
        throw new DecompressError('Compressed frame without size header');
    const expectedSize = payload.readUInt32LE(0);
    const compressedData = payload.subarray(4);
    try {
        const out = zlib_1.default.inflateRawSync(compressedData, {
            finishFlush: zlib_1.default.constants.Z_SYNC_FLUSH,
            maxOutputLength: Math.max(expectedSize * 2, 1024 * 1024),
        });
        if (out.length !== expectedSize) {
            throw new DecompressError(`inflate size mismatch: got ${out.length}, expected ${expectedSize}`);
        }
        return out;
    }
    catch (err) {
        const message = err instanceof Error ? err.message : 'inflate failed';
        throw new DecompressError(message);
    }
}
function buildCloseAckFrame(closePayload) {
    const firstByte = 0x80 | exports.OPCODE_CLOSE;
    const len = closePayload.length;
    if (len <= 125) {
        return Buffer.concat([Buffer.from([firstByte, len]), closePayload]);
    }
    const lenBuf = Buffer.alloc(2);
    lenBuf.writeUInt16BE(len, 0);
    return Buffer.concat([Buffer.from([firstByte, 126]), lenBuf, closePayload]);
}
